import React from "react";
import { Check, AlertTriangle, Sparkles, Calendar, Wrench, Leaf } from "lucide-react";

export const ServiceRichContent = ({ content }) => {
  if (!content) return null;
  return (
    <>
      {/* Long intro */}
      {content.longIntro?.length > 0 && (
        <section className="py-16 md:py-20 border-t border-koning-border" data-testid="rich-long-intro">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20 space-y-5 text-koning-text/90 text-base md:text-lg leading-relaxed">
            {content.longIntro.map((p) => <p key={p.slice(0, 60)}>{p}</p>)}
          </div>
        </section>
      )}

      {/* Sections (H2s) */}
      {content.sections?.length > 0 && (
        <section className="py-12 md:py-16 border-t border-koning-border" data-testid="rich-sections">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20 space-y-16">
            {content.sections.map((s) => (
              <article key={s.heading}>
                <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">{s.heading}</h2>
                <div className="mt-5 space-y-4 text-koning-muted leading-relaxed text-base md:text-lg">
                  {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                </div>
                {s.bullets?.length > 0 && (
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-3 text-koning-text">
                        <Check size={18} className="text-koning-gold mt-1 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Cost table intentionally omitted — richtprijzen worden apart per dienst
          bevestigd voordat ze worden getoond. Ondertussen verwijzen wij naar
          "Offerte op maat" via de CTA-buttons. */}

      {/* Materials */}
      {content.materials?.length > 0 && (
        <section className="py-16 md:py-20" data-testid="rich-materials">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex items-center gap-3 mb-6">
              <Wrench className="text-koning-gold" size={20} />
              <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">Materialen & afwerking</h2>
            </div>
            {content.materialsIntro && <p className="text-koning-muted leading-relaxed mb-8 max-w-3xl">{content.materialsIntro}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {content.materials.map((m) => (
                <div key={m.title} className="border border-koning-border p-5">
                  <div className="font-display text-white text-lg">{m.title}</div>
                  <p className="text-koning-muted text-sm mt-1.5 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Timeline */}
      {content.timeline?.length > 0 && (
        <section className="py-16 md:py-20 bg-koning-surface border-y border-koning-border" data-testid="rich-timeline">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="text-koning-gold" size={20} />
              <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">Doorlooptijd per fase</h2>
            </div>
            {content.timelineIntro && <p className="text-koning-muted leading-relaxed mb-8 max-w-3xl">{content.timelineIntro}</p>}
            <ol className="space-y-6">
              {content.timeline.map((t, i) => (
                <li key={t.phase} className="grid grid-cols-[80px_1fr] md:grid-cols-[120px_1fr_120px] gap-4 md:gap-6 border-b border-koning-border pb-6 last:border-0">
                  <div className="font-display text-koning-gold text-3xl md:text-4xl">{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="font-display text-white text-xl">{t.phase}</div>
                    <p className="text-koning-muted text-sm md:text-base mt-1.5 leading-relaxed">{t.desc}</p>
                  </div>
                  <div className="hidden md:block text-xs tracking-[0.2em] uppercase text-koning-muted self-center text-right">{t.duration}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Mistakes to avoid */}
      {content.mistakes?.length > 0 && (
        <section className="py-16 md:py-20" data-testid="rich-mistakes">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="text-koning-gold" size={20} />
              <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">Veelgemaakte fouten — en hoe wij ze voorkomen</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.mistakes.map((m, i) => (
                <div key={m.title} className="border border-koning-border p-6 flex gap-4">
                  <div className="font-display text-koning-gold text-3xl leading-none">{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="font-display text-white text-lg">{m.title}</div>
                    <p className="text-koning-muted text-sm mt-1.5 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Subsidies */}
      {content.subsidies && (
        <section className="py-16 md:py-20 bg-koning-surface border-y border-koning-border" data-testid="rich-subsidies">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex items-center gap-3 mb-6">
              <Leaf className="text-koning-gold" size={20} />
              <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">Subsidies & financieel voordeel</h2>
            </div>
            <p className="text-koning-muted leading-relaxed max-w-3xl">{content.subsidies}</p>
          </div>
        </section>
      )}

      {/* De "Bekend onder"-sectie (keyword-lijst) is definitief verwijderd
          om iedere vorm van keyword stuffing te voorkomen. */}
    </>
  );
};
